import { LogLevel } from './log-level';

/** Runtime override for the **console** floor. */
export const CONSOLE_LEVEL_KEY = 'pcp-log-level';

/**
 * Runtime override for the **telemetry** floor.
 *
 * Separate from the console key on purpose: the two situations want opposite things. Local
 * development wants a verbose console and no extra ingestion; diagnosing a problem on an
 * installation you cannot reproduce wants verbose telemetry from a machine whose console you
 * cannot see.
 */
export const TELEMETRY_LEVEL_KEY = 'pcp-telemetry-level';

/** Cache duration in ms — avoids reading localStorage on every log call. */
const CACHE_TTL_MS = 5_000;

interface CacheEntry { level: LogLevel | null; expiresAt: number }
const cache = new Map<string, CacheEntry>();

/**
 * Reads a severity-floor override from localStorage (cached for 5 seconds, per key).
 *
 * ```
 * localStorage.setItem('pcp-log-level', 'VERBOSE')                                   // console
 * localStorage.setItem('pcp-telemetry-level', 'DEBUG')                               // telemetry
 * localStorage.setItem('pcp-telemetry-level-expires-at', String(Date.now() + 3.6e6)) // lapses in 1h
 * ```
 *
 * Set an expiry for anything you turn on away from your own machine. Nothing downstream limits a
 * floor that is left low — the SDK applies no filtering of its own.
 */
export function getLocalStorageLogLevel(key: string = CONSOLE_LEVEL_KEY): LogLevel | null {
    const now = Date.now();
    const hit = cache.get(key);
    if (hit && now < hit.expiresAt) {
        return hit.level;
    }

    const level = readFromLocalStorage(key);
    cache.set(key, { level, expiresAt: now + CACHE_TTL_MS });
    return level;
}

/** Test seam — the 5s cache otherwise makes consecutive assertions in one spec unreliable. */
export function clearLogLevelCache(): void {
    cache.clear();
}

function readFromLocalStorage(key: string): LogLevel | null {
    try {
        const raw = localStorage.getItem(key);
        if (raw === null) {
            return null;
        }

        const expiryKey = `${key}-expires-at`;
        const expiresAt = localStorage.getItem(expiryKey);
        if (expiresAt && Number.parseInt(expiresAt, 10) <= Date.now()) {
            localStorage.removeItem(key);
            localStorage.removeItem(expiryKey);
            return null;
        }

        // `typeof === 'number'` rejects the numeric enum's reverse mappings: `LogLevel['2']` is the
        // *string* `'INFO'`, and returning that poisons every `level >= floor` comparison into NaN,
        // silencing the logger completely. Very reachable here — `localStorage` is typed by hand,
        // and guessing that levels are numeric is the obvious mistake to make.
        const level = LogLevel[raw.toUpperCase() as keyof typeof LogLevel];
        return typeof level === 'number' ? level : null;
    } catch {
        return null;
    }
}
